import { useState, FormEvent, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d758pej41deorl02f6vg';

const treatTypes = ['Custom Cake', 'Cookies', 'Pastries', 'Pies', 'Cheesecake', 'Cake Pops', 'Cupcakes', 'Other'];

const howItWorks = [
  {
    step: '01',
    icon: 'ri-message-3-line',
    title: 'Submit Your Request',
    desc: 'Fill out our simple order form with your vision, event details, and any design inspiration you have in mind.',
  },
  {
    step: '02',
    icon: 'ri-discuss-line',
    title: 'We Connect With You',
    desc: 'Sherry will reach out personally to discuss your design, flavors, and all the details that make your treat uniquely yours.',
  },
  {
    step: '03',
    icon: 'ri-magic-line',
    title: 'We Create the Magic',
    desc: 'Your order is handcrafted with premium ingredients, artistic skill, and a whole lot of love.',
  },
  {
    step: '04',
    icon: 'ri-cake-3-line',
    title: 'Enjoy & Celebrate',
    desc: 'Pick up your beautiful creation and enjoy every delicious moment of your celebration!',
  },
];

const popularTreats = [
  {
    label: 'Custom Cakes',
    img: 'https://public.readdy.ai/ai/img_res/568ff37fad54763a54b6e39fb55a1d1c.jpg',
  },
  {
    label: 'Decorated Cookies',
    img: 'https://readdy.ai/api/search-image?query=gorgeous%20royal%20icing%20decorated%20sugar%20cookies%20in%20pink%20white%20gold%20assorted%20shapes%20hearts%20flowers%20monograms%20flat%20lay%20food%20photography%2C%20elegant%20bakery%20display%2C%20pastel%20wedding%20baby%20shower%20cookies&width=400&height=350&seq=order002&orientation=portrait',
  },
  {
    label: 'Pies & Cheesecakes',
    img: 'https://static.readdy.ai/image/b56ddf5676badcd01c30e06215815ec0/9ca8537e7d8614358917eafe713ae7ff.jpeg',
  },
  {
    label: 'Cake Pops & Pastries',
    img: 'https://readdy.ai/api/search-image?query=beautiful%20assorted%20cake%20pops%20and%20French%20pastries%20arranged%20elegantly%20pink%20white%20gold%20decorated%20with%20sprinkles%20edible%20pearls%2C%20professional%20food%20photography%2C%20party%20treats%2C%20sweet%20celebrations%20bakery%2C%20premium%20dessert%20display&width=400&height=350&seq=order003&orientation=portrait',
  },
];

export default function CustomOrdersPage() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = heroRef.current;
    if (!el) return;
    setTimeout(() => {
      el.classList.remove('opacity-0', 'translate-y-6');
      el.classList.add('opacity-100', 'translate-y-0');
    }, 100);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.value) {
        data.append(input.name, input.value);
      }
    });
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20array%20of%20custom%20cakes%20cookies%20and%20pastries%20artfully%20arranged%20on%20elegant%20marble%20table%20blush%20pink%20gold%20cream%20color%20palette%2C%20luxury%20patisserie%20display%2C%20professional%20bakery%20photography%2C%20stunning%20dessert%20spread%2C%20celebration%20ready%2C%20warm%20inviting%20Texas%20bakery&width=1920&height=700&seq=orders-hero001&orientation=landscape"
            alt="Custom order desserts"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/85 to-charcoal-900/40" />
        </div>
        <div
          ref={heroRef}
          className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 transition-all duration-1000 ease-out opacity-0 translate-y-6"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-gold-400" />
            <span className="text-gold-400 text-xs uppercase tracking-[0.3em] font-sans font-semibold">Handcrafted for You</span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl text-white leading-tight mb-6">
            Custom Orders<br />
            <em className="italic text-blush-300">Made to Match Your Vision</em>
          </h1>
          <p className="text-cream-200 text-lg font-sans font-light leading-relaxed max-w-2xl mb-8">
            From elegant multi-tier wedding cakes to fun birthday treats, every Sweet Celebrations creation is custom-designed and made from scratch just for you.
          </p>
          <a
            href="#order-form"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 bg-blush-500 hover:bg-blush-600 text-white px-8 py-4 rounded-full font-sans font-semibold text-sm tracking-wide whitespace-nowrap transition-all duration-200 hover:scale-105 cursor-pointer"
          >
            Request Your Order Now
            <i className="ri-arrow-down-line" />
          </a>
        </div>
      </section>

      {/* Popular treats */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl text-charcoal-800 mb-3">
              What We <em className="italic text-blush-500">Create</em>
            </h2>
            <p className="text-warm-gray font-sans text-base font-light">
              All treats are custom-designed and made to order — no two are exactly alike.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {popularTreats.map((t) => (
              <div key={t.label} className="group relative rounded-2xl overflow-hidden cursor-pointer">
                <div className="w-full h-56 overflow-hidden">
                  <img
                    src={t.img}
                    alt={t.label}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/70 to-transparent flex items-end p-4">
                  <span className="text-white font-serif text-base">{t.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-warm-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-gold-500" />
              <span className="text-gold-500 text-xs uppercase tracking-[0.25em] font-sans font-semibold">Simple Process</span>
              <div className="h-px w-10 bg-gold-500" />
            </div>
            <h2 className="font-serif text-4xl text-charcoal-800 mb-4">
              How It <em className="italic text-blush-500">Works</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step) => (
              <div key={step.step} className="bg-white rounded-2xl p-8 border border-warm-200 relative">
                <span className="absolute top-5 right-6 font-serif text-5xl text-blush-100 font-bold leading-none select-none">{step.step}</span>
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blush-500 text-white mb-5 relative z-10">
                  <i className={`${step.icon} text-lg`} />
                </div>
                <h3 className="font-serif text-xl text-charcoal-800 mb-3 relative z-10">{step.title}</h3>
                <p className="text-warm-gray text-sm font-sans leading-relaxed relative z-10">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Order Form */}
      <section id="order-form" className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-gold-500" />
              <span className="text-gold-500 text-xs uppercase tracking-[0.25em] font-sans font-semibold">Let&apos;s Get Started</span>
              <div className="h-px w-10 bg-gold-500" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal-800 mb-4">
              Request Your<br /><em className="italic text-blush-500">Custom Order</em>
            </h2>
            <p className="text-warm-gray text-base font-sans font-light leading-relaxed">
              Tell us everything about your celebration. The more detail, the better — we love bringing big ideas to life.
            </p>
          </div>

          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-3">
                <i className="ri-check-line text-xl" />
              </div>
              <h3 className="font-serif text-xl text-green-800 mb-2">Your Request is Sent!</h3>
              <p className="text-green-700 text-sm font-sans">
                Thank you so much! We&apos;ll be in touch with you very soon to start creating something amazing.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-center">
              <p className="text-red-700 text-sm font-sans">Something went wrong. Please try again or reach out on social media.</p>
            </div>
          )}

          <form
            data-readdy-form
            id="custom-order-page-form"
            onSubmit={handleSubmit}
            className="bg-cream-50 rounded-2xl p-8 md:p-10 flex flex-col gap-5 border border-warm-200"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-charcoal-800 text-sm font-sans font-semibold">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Your name"
                  className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white placeholder-warm-gray"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-charcoal-800 text-sm font-sans font-semibold">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white placeholder-warm-gray"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-charcoal-800 text-sm font-sans font-semibold">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="(806) 555-0000"
                  className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white placeholder-warm-gray"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-charcoal-800 text-sm font-sans font-semibold">Event Date *</label>
                <input
                  type="date"
                  name="event_date"
                  required
                  className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white text-charcoal-800"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-charcoal-800 text-sm font-sans font-semibold">Type of Treat *</label>
                <select
                  name="treat_type"
                  required
                  className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white text-charcoal-800 cursor-pointer"
                >
                  <option value="">Select a treat...</option>
                  {treatTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-charcoal-800 text-sm font-sans font-semibold">Number of Servings</label>
                <input
                  type="number"
                  name="servings"
                  min="1"
                  placeholder="e.g. 24"
                  className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white placeholder-warm-gray"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Theme or Occasion</label>
              <input
                type="text"
                name="theme_occasion"
                placeholder="e.g. Princess Birthday, Rustic Wedding, Baby Shower..."
                className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white placeholder-warm-gray"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">
                Design Ideas &amp; Description
                <span className={`ml-2 text-xs font-normal ${charCount > 500 ? 'text-red-500' : 'text-warm-gray'}`}>
                  ({charCount}/500)
                </span>
              </label>
              <textarea
                name="design_description"
                rows={5}
                maxLength={500}
                placeholder="Describe your dream design — colors, themes, flavors, any inspiration you have. The more detail the better!"
                className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white placeholder-warm-gray resize-none"
                onChange={(e) => setCharCount(e.target.value.length)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Upload Inspiration Photo</label>
              <div className="border-2 border-dashed border-blush-200 rounded-lg p-6 text-center bg-blush-50/50">
                <i className="ri-image-add-line text-3xl text-blush-400 mb-2 block" />
                <p className="text-warm-gray text-sm font-sans">Photo uploads are not collected online.</p>
                <p className="text-warm-gray text-xs font-sans mt-1">Please describe your inspiration above, or share a reference image via Facebook or Instagram message.</p>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Preferred Budget</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-warm-gray font-sans text-sm">$</span>
                <input
                  type="text"
                  name="budget"
                  placeholder="e.g. 50–200"
                  className="border border-warm-200 rounded-lg pl-8 pr-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-white placeholder-warm-gray w-full"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status === 'submitting' || charCount > 500}
              className="mt-2 w-full bg-blush-500 hover:bg-blush-600 disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-full font-sans font-semibold text-sm tracking-wide whitespace-nowrap flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.01] cursor-pointer"
            >
              {status === 'submitting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Sending Your Request...
                </>
              ) : (
                <>
                  Submit Your Sweet Order Request
                  <i className="ri-send-plane-line" />
                </>
              )}
            </button>

            <p className="text-warm-gray text-xs text-center font-sans">
              We&apos;ll respond within 1–2 business days. For urgent orders, please reach out on{' '}
              <a href="https://www.facebook.com/scbakeacake" target="_blank" rel="nofollow noopener noreferrer" className="text-blush-500 hover:underline cursor-pointer">Facebook</a>.
            </p>
          </form>
        </div>
      </section>

      {/* Social links at bottom */}
      <section className="py-12 bg-warm-100">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <p className="text-charcoal-800 font-serif text-xl mb-6">Connect with us on social media</p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="https://www.facebook.com/scbakeacake"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white px-6 py-3 rounded-full font-sans font-semibold text-sm whitespace-nowrap transition-all duration-200 cursor-pointer"
            >
              <i className="ri-facebook-fill" />
              Facebook
            </a>
            <a
              href="https://www.instagram.com/sherry_sweet_celebrations/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blush-500 hover:bg-blush-600 text-white px-6 py-3 rounded-full font-sans font-semibold text-sm whitespace-nowrap transition-all duration-200 cursor-pointer"
            >
              <i className="ri-instagram-line" />
              Instagram
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
